import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';

/* Example Usage:
 *   this.toastService.success('Hello World Success!');
 *   this.toastService.info('Hello World Info!');
 *   this.toastService.warning('Hello World Warning!', { timeout: 3000 });
 *   this.toastService.error('Hello World Error!', { timeout: 8000 } );
 */

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    toasts: ToastData[] = [];
    defaultTimeout: number;

    public stopImpersonationSubject = new BehaviorSubject(false);

    constructor(private router: Router) {
        this.defaultTimeout = 6000;
    }

    public get() {
        return this.toasts;
    }

    //
    // Methods used to trigger and create a toast
    //

    // Creates the actual toast and assigns behavior based on toast.options
    public makeToast(toast: ToastData) {
        if (!toast.options) {
            toast.options = {
                dismiss: true,
            };
        }

        if (toast.options && (toast.options.dismiss == null || toast.options.dismiss == undefined))
            toast.options.dismiss = true;

        // Handle toast 'logic' here:
        if (toast.options && toast.options.logic) {
            if (toast.options.logic == 'impersonation') {
                this.stopImpersonationSubject.next(false);
            }

            // .. additional logic conditions checked below
        }

        if (toast.alert) {
            if (toast.options) {
                if (!toast.options.timeout) {
                    toast.options.timeout = 0;
                }
            } else {
                toast.options = {
                    timeout: 0,
                };
            }
            this.toasts.unshift(toast);
        } else {
            this.toasts.push(toast);
        }

        this.handleToast(toast);
    }

    // Create a success toast
    public success(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'success',
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Create an info toast
    public info(message: string, options?: ToastOptions, id?: number) {
        let toast: ToastData = {
            text: message,
            type: 'info',
            options: options ? options : null,
            id: id,
        };

        this.makeToast(toast);
    }

    // Create a warning toast
    public warning(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'warning',
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Create an error toast
    public error(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'error',
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    //
    // Alerts
    //
    // Create a success alert. This extends the behavior of a normal toast.
    public successAlert(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'success',
            alert: true,
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Create an info alert. This extends the behavior of a normal toast.
    public infoAlert(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'info',
            alert: true,
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Create a warning alert. This extends the behavior of a normal toast.
    public warningAlert(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'warning',
            alert: true,
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Create an error alert. This extends the behavior of a normal toast.
    public errorAlert(message: string, options?: ToastOptions) {
        let toast: ToastData = {
            text: message,
            type: 'error',
            alert: true,
            options: options ? options : null,
        };

        this.makeToast(toast);
    }

    // Removes a specific toast
    public remove(toast: ToastData) {
        let index = this.toasts.indexOf(toast);

        if (index > -1) {
            this.toasts.splice(index, 1);
        }
    }

    public removeIfExists(id?: number) {
        try {
            if (id) {
                let matchingToast = this.toasts.filter((x) => x.id && x.id == id);

                if (matchingToast && matchingToast.length == 1) {
                    this.remove(matchingToast[0]);
                }
            }
        } catch (e) {}
    }

    public resetAll() {
        this.toasts = [];
    }

    // Remove all toasts (by default, error does not timeout!)
    public removeErrors() {
        this.toasts = this.toasts.filter((t) => t.type !== 'error');
    }

    // Handle the timeout value of a toast/alert and removes after expiration
    private async handleToast(toast: ToastData) {
        let timeout =
            toast.options && toast.options.timeout != null && toast.options.timeout != undefined
                ? toast.options.timeout
                : null;

        if (timeout !== 0) {
            await this.delay(timeout);

            if (this.toasts.indexOf(toast) > -1) {
                this.remove(toast);
            }
        }
    }

    // Delay the toast removal based on toast.options.timeout value.
    private delay(timeout?: number) {
        let delayTimeout = timeout ? timeout : this.defaultTimeout;
        return new Promise((resolve) => setTimeout(resolve, delayTimeout));
    }

    // Handle logic assigned to a toast in the toast.options.logic value.
    public takeAction(toast: ToastData) {
        if (toast.options.logic) {
            let logic = toast.options.logic;

            if (logic === 'impersonation') {
                this.stopImpersonationSubject.next(true);
            } else if (logic === 'refresh') {
                window.location.reload();
            } else if (logic == 'navigate') {
                this.router.navigate([toast.options.location]);
            }
        }
        this.remove(toast);
    }

    // Returns the current number of alerts on the page.
    // Used by app.component to determine content top margin.
    public getNumberOfAlerts() {
        let alerts = this.toasts.filter((t) => t.alert);
        return alerts.length;
    }
}

export interface ToastData {
    text: string;
    type?: string;
    alert?: boolean;
    id?: number;
    options?: ToastOptions;
}

export interface ToastOptions {
    dismiss?: boolean;
    timeout?: number;
    actionText?: string;
    location?: string;
    logic?: string;
    actionAndDismiss?: boolean;
}
